import { Dispatch } from 'react';
import { cloneDeep, set } from 'lodash';

// This is a basic, reusable reducer to handle updates to the state when using `useReducer`.
// It is called by the `useReducer` `dispatch` function, and it accepts an array of objects.
// Each object should have:
//   - `type` - string, the key that is being updated.
//              This can be a string for a straightforward key, like `showError`
//              or it can contain dots to indicate a key inside an object, like `errors.form.name`
//   - ` value` - any, the new value for that key
//
// This reducer should be used in cases where we're using a reducer to handle a large amount of
// straightforward state, which would otherwise be handled by `useState`, to keep the code more readable
// and to do several state updates at once.
// This reducer should not be used in cases where we need to call functions or do any complicated checks or actions.

export interface IBasicReducerAction {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}
export type TDispatch = Dispatch<IBasicReducerAction | IBasicReducerAction[]>;

export interface IBasicReducerState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

type TBasicReducer = <S extends object>(
  state: S,
  action: IBasicReducerAction | IBasicReducerAction[],
) => S;

const basicReducer: TBasicReducer = (state, action) => {
  const actions = Array.isArray(action) ? action : [action];
  const newState = cloneDeep(state);
  actions.forEach(({ type, value }) => {
    set(newState, type, value);
  });

  return newState;
};

export default basicReducer;
